import _ from "lodash";
import xml from "csi/xml";
import csi from "csi/main";
import crews from "csi/access/crews";
import reflection from "csi/reflection";
import { C8AttributeValue, C8Node, ICache, cnl } from "csi/typings";
import resolver from "csi/resolver";
import sessionConstants from "csi/sessionConstants";
import locale from "csi/locale";
import ServerRollupScope, { IEvaluateParam, setServerTimeZoneOffset } from "./ServerRollupScope";
import Processor from "csi/middleware/rollup/Processor";

export interface ISessionVariables {
	debugMode: boolean;
	locationType: string;
	locationUrl: string;
	serverTimeZoneOffset: number;
}

let _sessionVariables: ISessionVariables = {
	debugMode: false,
	locationType: "",
	locationUrl: "",
	serverTimeZoneOffset: 0,
};

class ClientRollupScope extends ServerRollupScope {
	constructor(
		rp: Processor,
		node: C8Node,
		cache: ICache,
		value?: C8AttributeValue,
		params?: IEvaluateParam,
	) {
		super(rp, node, cache, value, params);
	}

	// client-side expression functions

	childAttr(s: string, defaultValue?: any): any {
		// summary: accepts server "." notation for attributes, and returns given defaultValue if path to attribute does not exist
		let node = this.activeNode;
		const attrs = s.split(".");
		const n = attrs.length - 1;
		if (n) {
			let url = null;

			for (var i = 0; i < n; ++i) {
				url = node[attrs[i]];

				if (defaultValue == null) {
					// if no default provided, then throw exception if it does not exist
					node = this._getNode(url);
				} else {
					// if default provided, then catch any thrown exception and return default
					try {
						node = this._getNode(url);
					} catch (ex) {
						node = null;
					}
				}

				if (node == null) {
					return defaultValue;
				}
			}

			s = attrs[n];
		}

		return this.attr(node.$URL, s);
	}

	classSetting(clazz: string, settingAttr: string): C8AttributeValue {
		return reflection.getClassSetting(clazz, settingAttr);
	}

	/**
	 * Return URL of result node in current context, regardless of any paths traversed
	 */
	contextNode(): cnl {
		return this.rp.getResultNodeURL();
	}

	/**
	 * Return node for which expression is being evaluated (which is different than returning URL as with "thisNode" and "contextNode")
	 * NOTE: this is used in combination with "module" function and should be deprecated;
	 * we should rework any use of "module" in expressions so that rollup engine is fully contained
	 */
	currentNode(): C8Node {
		return this._currentNode;
	}

	currentCompany(): string {
		return sessionConstants.userCompanyUrl;
	}

	currentDayStartTime(): number {
		const dt = new Date(this.rp.timeCurrent);
		dt.setHours(0, 0, 0, 0);
		return dt.getTime();
	}

	currentRoleName(): string {
		return sessionConstants.roleName;
	}

	hasGlobalRole(roles: string | string[]): boolean {
		return crews.isInGlobalRoles(_.isArray(roles) ? roles : [roles]);
	}

	hasRole(roles: string | string[]): boolean {
		return crews.isInRoles(_.isArray(roles) ? roles : [roles], this._currentNode);
	}

	hostCompany(): string {
		return reflection.URL_COMPANY;
	}

	isAdmin(): boolean {
		return sessionConstants.admin;
	}

	isCustomerUser(): boolean {
		return sessionConstants.isCustomerUser;
	}

	isDebug(): boolean {
		return _sessionVariables.debugMode;
	}

	isFrozen(url: string): boolean {
		const node = url ? this._getNode(url) : this.activeNode;
		return xml.nodeIsFrozen(node);
	}

	isSalesDivisionUser(): boolean {
		return sessionConstants.isSalesDivisionUser;
	}

	isSetupViewer(): boolean {
		return sessionConstants.setupViewer;
	}

	isSupplierUser(): boolean {
		return sessionConstants.isSupplierUser;
	}

	localize(s: string): string {
		return locale.resource(s);
	}

	locationNode(type: string): string {
		const url = _sessionVariables.locationUrl;

		if (type == null) {
			return url;
		}

		const node = resolver.resolveAncestor(resolver.resolveNode(url), type);
		if (node) {
			return node.$URL;
		}

		return null;
	}

	locationType(): string {
		return _sessionVariables.locationType;
	}

	/*
	* Return specified module and call function if specified - throws error if not already loaded
	*/
	module(mid: string, f: string): any {
		const obj = csi.syncRequire(mid);
		return f ? obj[f](this) : obj;
	}

	mult(a: any[], id?: string): any {
		const values = this._getValues(a, id);
		let x = 1;

		values.forEach((n) => {
			x *= n;
		});

		return x;
	}

	setting(url: string, idSetting: string): any {
		// handle both cases where node is given or not - if not, then expression is the first parameter
		const node = idSetting ? this._getNode(url) : this.activeNode;
		if (!node) return null;

		const x = reflection.getNodeSetting(node, idSetting || url);
		return x;
	}

	sum(a: any[], id?: string): any {
		const values = this._getValues(a, id);
		let x = 0;

		values.forEach((n) => {
			x += n;
		});

		return x;
	}

	validValue(idAttr: string): boolean {
		return xml.nodeAttributeIsValueValid(this.activeNode, idAttr);
	}
}

export function setSessionVariables(vars: ISessionVariables): void {
	_sessionVariables = vars;
	setServerTimeZoneOffset(_sessionVariables.serverTimeZoneOffset);
}

export default ClientRollupScope;